import React from 'react';
import { CalendarEvent } from '../../../types/calendar';
import { Order } from '../../../types/order';
import {
  getWeekDates,
  formatDateToISO,
  isToday,
  getEventsForDate,
  sortEventsByTime
} from '../../../utils/calendarUtils';
import { getDaysSinceOrder, getOrderRowBackgroundColor } from '../../../utils/orderUtils';
import EventCard from './EventCard';

interface WeekViewProps {
  currentDate: string;
  events: CalendarEvent[];
  orders: Order[];
  onEventClick: (event: CalendarEvent) => void;
}

const WeekView: React.FC<WeekViewProps> = ({ currentDate, events, orders, onEventClick }) => {
  const weekDates = getWeekDates(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getOrdersForDate = (dateStr: string): Order[] => {
    return orders.filter(order => order.orderDate === dateStr);
  };

  const renderDayColumn = (date: Date, dayName: string) => {
    const dateStr = formatDateToISO(date);
    const isTodayDate = isToday(date);
    const allDayEvents = sortEventsByTime(getEventsForDate(events, dateStr));

    // Sort events: incomplete first, completed last (for deliveries)
    const dayEvents = allDayEvents.sort((a, b) => {
      const aCompleted = a.completed ? 1 : 0;
      const bCompleted = b.completed ? 1 : 0;
      return aCompleted - bCompleted;
    });

    const dayOrders = getOrdersForDate(dateStr);

    return (
      <div key={dateStr} style={styles.dayColumn}>
        <div
          style={{
            ...styles.dayHeader,
            ...(isTodayDate ? styles.todayHeader : {})
          }}
        >
          <div style={styles.dayName}>{dayName}</div>
          <div
            style={{
              ...styles.dayNumber,
              ...(isTodayDate ? styles.todayNumber : {})
            }}
          >
            {date.getDate()}
          </div>
        </div>

        <div style={styles.dayContent}>
          {dayOrders.length > 0 && (
            <div style={styles.orderSection}>
              <div style={styles.orderSectionTitle}>Orders ({dayOrders.length})</div>
              {dayOrders.map(order => {
                const daysSince = getDaysSinceOrder(order.orderDate);
                return (
                  <div
                    key={order.id}
                    style={{
                      ...styles.orderItem,
                      backgroundColor: getOrderRowBackgroundColor(daysSince, order.status)
                    }}
                  >
                    <div style={styles.orderCustomer}>{order.customerName}</div>
                    <div style={styles.orderStatus}>{order.status}</div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.eventsSection}>
            {dayEvents.map(event => (
              <EventCard key={event.id} event={event} onClick={onEventClick} />
            ))}
            {dayEvents.length === 0 && dayOrders.length === 0 && <div style={styles.emptyState}>No events</div>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.weekGrid}>{weekDates.map((date, index) => renderDayColumn(date, weekDays[index]))}</div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto'
  },
  weekGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    height: '100%',
    gap: '1px',
    backgroundColor: '#e0e0e0'
  },
  dayColumn: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    minHeight: '500px'
  },
  dayHeader: {
    padding: '12px',
    textAlign: 'center',
    borderBottom: '2px solid #e0e0e0',
    backgroundColor: '#fafafa'
  },
  todayHeader: {
    backgroundColor: '#e3f2fd'
  },
  dayName: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase'
  },
  dayNumber: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    marginTop: '4px'
  },
  todayNumber: {
    color: '#2196F3'
  },
  dayContent: {
    padding: '12px',
    flex: 1,
    overflow: 'auto'
  },
  orderSection: {
    marginBottom: '16px',
    padding: '8px',
    backgroundColor: 'transparent',
    borderRadius: '4px',
    border: '1px solid rgba(0, 0, 0, 0.1)'
  },
  orderSectionTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#FF9800',
    marginBottom: '8px'
  },
  orderItem: {
    padding: '6px',
    borderRadius: '3px',
    marginBottom: '4px',
    fontSize: '12px'
  },
  orderCustomer: {
    fontWeight: '500',
    color: '#333',
    marginBottom: '2px'
  },
  orderStatus: {
    fontSize: '11px',
    color: '#666'
  },
  eventsSection: {
    display: 'flex',
    flexDirection: 'column'
  },
  emptyState: {
    fontSize: '12px',
    color: '#999',
    textAlign: 'center',
    padding: '20px'
  }
};

export default WeekView;
